import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, InputLabel,
  MenuItem, Select, Stack, TextField, Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Line, LineChart, ResponsiveContainer, Tooltip,
  XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { useSiteControllerHeartbeats } from '../../api/queries';
import type { HeartbeatPoint, HeartbeatsQuery } from '../../api/types';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';

/**
 * Stage 3 §M-T8.5 — per-SC heartbeat panel.
 *
 * Three stacked surfaces, all driven by the same range query:
 *   1. Range controls: From / To pickers + row-limit selector.
 *   2. Chart: CPU% / Memory% / Disk-free time series (Recharts).
 *   3. Tabular subform: per-row received_at, runtime, cpu%, mem%, disk
 *      free, temp, alerts. Backs the chart with the raw data.
 *
 * Default window: last 48 h, 3000 rows (server-side cap 5000). Cloud
 * returns truncated=true if the row count hits the limit — we surface a
 * warning so the user knows the view is clipped.
 */
export function SiteControllerHeartbeatPanel({ siteControllerId }: { siteControllerId: string }) {
  // The query string is owned by the panel: pickers update local state,
  // which flips the React Query key and refetches. Empty values fall
  // back to server-side defaults.
  const [fromInput, setFromInput] = useState<string>('');
  const [toInput,   setToInput]   = useState<string>('');
  const [limit,     setLimit]     = useState<number>(3000);

  const query: HeartbeatsQuery = useMemo(() => ({
    from:  fromInput ? new Date(fromInput).toISOString() : undefined,
    to:    toInput   ? new Date(toInput).toISOString()   : undefined,
    limit: limit,
  }), [fromInput, toInput, limit]);

  const q = useSiteControllerHeartbeats(siteControllerId, query);
  const points = useMemo(() => q.data?.points ?? [], [q.data?.points]);

  const chartData = useMemo(() => points.map(p => ({
    t: new Date(p.receivedAt).getTime(),
    cpu: p.cpuPercent ?? null,
    memPct: p.memUsedMb && p.memTotalMb && p.memTotalMb > 0
      ? Number(((p.memUsedMb / p.memTotalMb) * 100).toFixed(1))
      : null,
    // Plot disk as USED % (not free GB) so it shares the 0-100 axis with
    // cpu/mem and matches the used-% strips/grid — a free-GB line (e.g. 39 GB)
    // on its own axis flattened every other series to the baseline.
    diskUsedPct: diskUsedPct(p.diskFreeBytes, p.diskTotalBytes),
    dataDiskUsedPct: diskUsedPct(p.dataDiskFreeBytes, p.dataDiskTotalBytes),
    onlineMachines: p.onlineMachines ?? null,
  })), [points]);

  // ESP32 Deployment Plan §3 M7 — derive the M7 stop-point indicator
  // from the most-recent heartbeat. > 0 means at least one HAT is
  // talking SAS to the SC; we surface this as a bright-green banner so
  // a field tech wiring up the bench rig has unmistakable feedback.
  const latestOnlineMachines = points.length > 0
    ? points[points.length - 1].onlineMachines
    : null;

  const tableRows = useMemo(() => points
    // Display newest-first in the table (chart wants oldest-first for x-axis).
    .slice().reverse()
    .map((p, i) => ({ id: `${p.receivedAt}-${i}`, ...p })),
    [points]);

  const cols: GridColDef[] = useMemo(() => [
    { field: 'receivedAt', headerName: 'Received',  flex: 1.4, minWidth: 180,
      valueFormatter: (v) => fmtOrEmDash(fmtDateTime(v as string)) },
    { field: 'runtimeVersion', headerName: 'Runtime', flex: 0.8, minWidth: 120,
      valueFormatter: (v) => fmtOrEmDash(v as string | null) },
    { field: 'bootstrapVersion', headerName: 'SC App', flex: 0.8, minWidth: 110,
      valueFormatter: (v) => fmtOrEmDash(v as string | null) },
    { field: 'cpuPercent', headerName: 'CPU %',   flex: 0.6, minWidth: 90,
      type: 'number', align: 'right', headerAlign: 'right',
      valueFormatter: (v) => v == null ? '—' : `${(v as number).toFixed(1)}%` },
    // Issue #29 — denominator-aware memory cell: "11% (1.8 / 16.0 GB)" so the
    // percentage has a visible total. Sorts on the numeric percentage.
    { field: 'memPct', headerName: 'Memory', flex: 1, minWidth: 170,
      align: 'right', headerAlign: 'right',
      valueGetter: (_, row) => row.memUsedMb != null && row.memTotalMb != null && row.memTotalMb > 0
        ? Math.round((row.memUsedMb / row.memTotalMb) * 100)
        : null,
      renderCell: (p) => fmtMemStrip(p.row as HeartbeatPoint) },
    { field: 'diskPct', headerName: 'OS disk', flex: 1, minWidth: 230,
      align: 'right', headerAlign: 'right',
      valueGetter: (_, row) => diskUsedPct(row.diskFreeBytes, row.diskTotalBytes),
      renderCell: (p) => <DiskCell free={p.row.diskFreeBytes} total={p.row.diskTotalBytes} /> },
    { field: 'dataDiskPct', headerName: 'Data disk', flex: 1, minWidth: 230,
      align: 'right', headerAlign: 'right',
      valueGetter: (_, row) => diskUsedPct(row.dataDiskFreeBytes, row.dataDiskTotalBytes),
      renderCell: (p) => <DiskCell free={p.row.dataDiskFreeBytes} total={p.row.dataDiskTotalBytes} /> },
    { field: 'tempC', headerName: 'Temp', flex: 0.5, minWidth: 80,
      type: 'number', align: 'right', headerAlign: 'right',
      valueFormatter: (v) => v == null ? '—' : `${(v as number).toFixed(1)}°C` },
    { field: 'alertCount', headerName: 'Alerts', flex: 0.4, minWidth: 70,
      type: 'number', align: 'right', headerAlign: 'right' },
    // ESP32 Deployment Plan §3 M7 — HATs currently online per the SC's
    // SAS-liveness sampler.
    { field: 'onlineMachines', headerName: 'Machines online', flex: 0.6, minWidth: 110,
      type: 'number', align: 'right', headerAlign: 'right',
      valueFormatter: (v) => v == null ? '—' : String(v) },
  ], []);

  return (
    <Stack spacing={2}>
      {/* Range controls */}
      <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
        <TextField
          label="From"
          type="datetime-local"
          size="small"
          value={fromInput}
          onChange={(e) => setFromInput(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="To"
          type="datetime-local"
          size="small"
          value={toInput}
          onChange={(e) => setToInput(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Limit</InputLabel>
          <Select<number>
            label="Limit"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            <MenuItem value={500}>500</MenuItem>
            <MenuItem value={1000}>1000</MenuItem>
            <MenuItem value={3000}>3000 (default)</MenuItem>
            <MenuItem value={5000}>5000 (max)</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="text"
          disabled={!fromInput && !toInput && limit === 3000}
          onClick={() => { setFromInput(''); setToInput(''); setLimit(3000); }}
        >
          Reset
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {q.data
            ? `${q.data.points.length.toLocaleString()} row${q.data.points.length === 1 ? '' : 's'}` +
              (q.data.truncated ? ` (truncated to ${q.data.limit.toLocaleString()})` : '')
            : ''}
        </Typography>
        {/* Issue #29 — manual refresh of the heartbeat sub-panel. */}
        <MuiTooltip title="Refresh heartbeats">
          <span>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disableElevation
              aria-label="Refresh heartbeats"
              disabled={q.isRefetching}
              onClick={() => { void q.refetch(); }}
              startIcon={q.isRefetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              Refresh
            </Button>
          </span>
        </MuiTooltip>
      </Stack>

      {q.data?.truncated && (
        <Alert severity="warning" variant="outlined">
          The result was clipped to {q.data.limit.toLocaleString()} rows.
          Narrow the From/To window to see the full data.
        </Alert>
      )}

      {q.isLoading && (
        <Typography variant="body2" color="text.secondary">Loading heartbeats…</Typography>
      )}

      {!q.isLoading && points.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No heartbeats in the selected window. Widen the From/To range or wait for the next tick.
        </Typography>
      )}

      {points.length > 0 && (
        <>
          {/* ESP32 Deployment Plan §3 M7 — bright-green M7 stop-point
              banner when at least one HAT is online. Visual success
              surface per the feedback memory: not muted, not small. */}
          {latestOnlineMachines != null && latestOnlineMachines > 0 && (
            <Box sx={{
              p: 2, borderRadius: 1,
              bgcolor: 'success.light',
              border: 2, borderColor: 'success.main',
              color: 'success.contrastText',
            }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark' }}>
                ✓ M7 stop-point: {latestOnlineMachines} machine{latestOnlineMachines === 1 ? '' : 's'} online
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.dark', mt: 0.5 }}>
                The SC's SAS-liveness sampler has at least one HAT in <strong>online</strong> gateway state — first SAS frame received, machine flipped to <strong>in_service</strong>.
              </Typography>
            </Box>
          )}

          {/* Latest-tick summary strip */}
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <KeyValue label="Latest"       value={fmtOrEmDash(fmtDateTime(points[points.length - 1].receivedAt))} />
            <KeyValue label="Window start" value={fmtOrEmDash(fmtDateTime(points[0].receivedAt))} />
            <KeyValue label="Runtime"      value={fmtOrEmDash(points[points.length - 1].runtimeVersion)} />
            <KeyValue label="SC App"       value={fmtOrEmDash(points[points.length - 1].bootstrapVersion)} />
            <KeyValue label="CPU%"   value={fmtPctStrip(points[points.length - 1].cpuPercent)} />
            <KeyValue label="Memory" value={fmtMemStrip(points[points.length - 1])} />
            <KeyValue label="OS disk"   value={<DiskCell
              free={points[points.length - 1].diskFreeBytes}
              total={points[points.length - 1].diskTotalBytes} />} />
            <KeyValue label="Data disk" value={<DiskCell
              free={points[points.length - 1].dataDiskFreeBytes}
              total={points[points.length - 1].dataDiskTotalBytes} />} />
            <KeyValue label="Temp"   value={fmtTempStrip(points[points.length - 1].tempC)} />
            <KeyValue label="Machines online"
              value={latestOnlineMachines == null ? '—' : String(latestOnlineMachines)} />
          </Stack>

          {/* Chart */}
          <Box sx={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={v => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  minTickGap={50}
                />
                <YAxis yAxisId="pct" domain={[0, 100]} tickFormatter={v => `${v}%`} />
                <YAxis yAxisId="count" orientation="right" allowDecimals={false} tickFormatter={v => `${v}`} />
                <Tooltip
                  labelFormatter={v => new Date(Number(v)).toLocaleString()}
                  formatter={(value, name) => {
                    const n = String(name);
                    if (n === 'CPU%' || n === 'Memory%') return `${value}%`;
                    if (n === 'OS disk free' || n === 'Data disk free') return `${value} GB`;
                    return String(value);
                  }}
                />
                <Legend />
                <Line yAxisId="pct" type="monotone" dataKey="cpu"            stroke="#1976d2" strokeWidth={2} dot={false} name="CPU%" connectNulls />
                <Line yAxisId="pct" type="monotone" dataKey="memPct"         stroke="#9c27b0" strokeWidth={2} dot={false} name="Memory%" connectNulls />
                <Line yAxisId="pct" type="monotone" dataKey="diskUsedPct"     stroke="#2e7d32" strokeWidth={2} dot={false} name="OS disk used" connectNulls />
                <Line yAxisId="pct" type="monotone" dataKey="dataDiskUsedPct" stroke="#00897b" strokeWidth={2} dot={false} name="Data disk used" connectNulls />
                <Line yAxisId="count" type="monotone" dataKey="onlineMachines" stroke="#ed6c02" strokeWidth={2} dot={false} name="Machines online" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Tabular subform — newest-first. Plain DataGrid (not
              RouteDataGrid) because the data is already loaded in
              memory from the same useSiteControllerHeartbeats call
              that drives the chart; no second fetch needed. */}
          <Box sx={{ width: '100%', height: 360 }}>
            <DataGrid
              rows={tableRows}
              columns={cols}
              density="compact"
              autosizeOnMount
              autosizeOptions={{ includeHeaders: true, includeOutliers: true, expand: true }}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              disableRowSelectionOnClick
            />
          </Box>
        </>
      )}
    </Stack>
  );
}

function fmtPctStrip(v: number | null): string {
  return v != null ? `${v.toFixed(1)}%` : '—';
}

// Issue #29 — denominator-aware memory / disk labels so a bare "11%" reads
// as "11% (1.8 / 16.0 GB)". Null/zero-safe: when one of a paired set is null
// we degrade to the value we do have rather than rendering a misleading %.
// Exported for unit testing.
export function fmtMemStrip(p: HeartbeatPoint): string {
  if (p.memTotalMb == null || p.memUsedMb == null || p.memTotalMb === 0) {
    return p.memUsedMb != null ? `${p.memUsedMb} MB used` : '—';
  }
  const pct = Math.round((p.memUsedMb / p.memTotalMb) * 100);
  return `${pct}% (${(p.memUsedMb / 1024).toFixed(1)} / ${(p.memTotalMb / 1024).toFixed(1)} GB)`;
}

// B5 — shared by BOTH the OS-disk and data-disk strips/columns. Threshold
// mirrors the RM's edge-triggered site_controller.disk_pressure audit
// (HeartbeatEndpoints.cs): warning at >= 80% used, critical at >= 90%.
export type DiskStatus = 'ok' | 'warning' | 'critical';

export function diskUsedPct(free: number | null, total: number | null): number | null {
  return total != null && free != null && total > 0
    ? Math.round(((total - free) / total) * 100)
    : null;
}

function diskStatusFor(usedPct: number | null): DiskStatus {
  return usedPct != null && usedPct >= 90 ? 'critical'
    : usedPct != null && usedPct >= 80   ? 'warning'
    : 'ok';
}

export function fmtDiskStrip(
  free: number | null,
  total: number | null,
): { label: string; status: DiskStatus } {
  if (total == null || free == null || total === 0) {
    return { label: free != null ? `${(free / 1e9).toFixed(0)} GB free` : '—', status: 'ok' };
  }
  const usedBytes = total - free;
  const pct = diskUsedPct(free, total)!;
  return {
    label: `${pct}% (${(usedBytes / 1e9).toFixed(0)} / ${(total / 1e9).toFixed(0)} GB, ${(free / 1e9).toFixed(0)} GB free)`,
    status: diskStatusFor(pct),
  };
}

const DISK_STATUS_COLOR: Record<DiskStatus, string> = {
  ok: 'text.primary',
  warning: 'warning.main',
  critical: 'error.main',
};

// Exported for unit testing — renders one disk strip/cell, coloring the
// label by used-% severity so a field tech scanning the grid spots a
// filling disk without reading every number.
export function DiskCell({ free, total }: { free: number | null; total: number | null }) {
  const { label, status } = fmtDiskStrip(free, total);
  return (
    <Typography
      variant="body2"
      component="span"
      data-disk-status={status}
      sx={{ color: DISK_STATUS_COLOR[status], fontWeight: status === 'ok' ? 400 : 700 }}
    >
      {label}
    </Typography>
  );
}

function fmtTempStrip(v: number | null): string {
  return v != null ? `${v.toFixed(1)}°C` : '—';
}

function KeyValue({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" component="div">{value}</Typography>
    </Stack>
  );
}
